import { parseError, invalidRequestError, invalidParamsError } from './JsonRpcError';
import { truncate } from './logString';
import type { JsonRpcError } from './JsonRpcError';

// JSON-RPC の検証/パース失敗を表す単一概念。id はレスポンスのトップレベルのため error 本体には含まない。
// JsonRpcResult<T> の失敗側・Envelope の失敗バリアント・各 from() の失敗が全てこれを参照する。
type JsonRpcFailure = { ok: false; id: string | null; error: JsonRpcError };

// 失敗結果の共通ファクトリ
export function jsonRpcFailure(id: string | null, error: JsonRpcError): JsonRpcFailure {
  return { ok: false, id, error };
}

// JSON-RPC メッセージのパース/検証結果を運ぶ Result 型。
// 成功側は検証済みの値 T、失敗側は JsonRpcFailure（JSON-RPC のエラー）。
export type JsonRpcResult<T> = { ok: true; value: T } | JsonRpcFailure;

type JsonRpcNotification = { method: string; params: Record<string, unknown> | undefined };

// JSON-RPC エンベロープの検証結果（3概念: id付きリクエスト / Notification / パース失敗）
type Envelope =
  | { ok: true; kind: 'request'; value: JsonRpcRequest }
  | { ok: true; kind: 'notification'; value: JsonRpcNotification }
  | JsonRpcFailure;

// validate が抽出する「id を除いた検証済み JSON-RPC メッセージ本体（method + params）」。
// 構造は JsonRpcNotification と同一だが意味が異なる別概念。
// JsonRpcNotification は「id を持たないメッセージ」という最終形を指すのに対し、
// こちらは id 付きリクエスト経路（parseRequest）でも経由する検証途中の中間表現。
// 構造の重複を理由に安易に統合しないこと。
type JsonRpcMessageBody = { method: string; params: Record<string, unknown> | undefined };

export class JsonRpcRequest {
  private constructor(
    private readonly _id: string,
    private readonly _method: string,
    private readonly _params: Record<string, unknown>
  ) {}

  get id(): string {
    return this._id;
  }

  get method(): string {
    return this._method;
  }

  get params(): Record<string, unknown> {
    return this._params;
  }

  static parse(data: unknown): Envelope {
    // 本区間はテキストフレームのみ使用する想定。
    // バイナリフレームは仕様上送られないが、防御的に typeof で string をチェックする。
    if (typeof data !== 'string') {
      return jsonRpcFailure(null, parseError('non_string_frame', `non-string frame: ${typeof data}`));
    }

    let req: unknown;
    try {
      req = JSON.parse(data);
    } catch {
      return jsonRpcFailure(null, parseError('json_syntax_error', `parse error: ${truncate(data)}`));
    }

    if (req === null || typeof req !== 'object' || Array.isArray(req)) {
      return jsonRpcFailure(
        null,
        invalidRequestError('non_object_message', `non-object message: ${JSON.stringify(req)}`)
      );
    }

    const reqObj = req as Record<string, unknown>;

    if (!('id' in reqObj)) {
      return JsonRpcRequest.parseNotification(reqObj);
    }

    // JSON-RPC 2.0 仕様では id に string / number / null を許容するが、
    // 本プロジェクトでは string のみを正規の値として扱う（形式は不問）。
    // 仕様より意図的に厳格にする理由は次の二点。
    //   - id の型を string に固定して設計をシンプルにする。
    //   - 仕様外の number / null を早期に弾いて異常に気付けるようにする。
    const id = reqObj['id'];
    if (typeof id !== 'string') {
      return jsonRpcFailure(null, invalidRequestError('invalid_id_type', `invalid id type: ${typeof id}`));
    }

    return JsonRpcRequest.parseRequest(reqObj, id);
  }

  private static parseNotification(reqObj: Record<string, unknown>): Envelope {
    // id が無いメッセージは Notification 候補。jsonrpc / method / params が不正な場合は
    // Notification ではなく不正リクエストとして id: null のエラーを返す（JSON-RPC 2.0 仕様）。
    const result = JsonRpcRequest.validate(reqObj, null);
    if (!result.ok) return result;

    const notification: JsonRpcNotification = { method: result.value.method, params: result.value.params };
    return { ok: true, kind: 'notification', value: notification };
  }

  private static parseRequest(reqObj: Record<string, unknown>, id: string): Envelope {
    const result = JsonRpcRequest.validate(reqObj, id);
    if (!result.ok) return result;

    // id 付きリクエストでは params は必須
    if (result.value.params === undefined) {
      return jsonRpcFailure(id, invalidParamsError('missing_params', 'missing params'));
    }

    return { ok: true, kind: 'request', value: new JsonRpcRequest(id, result.value.method, result.value.params) };
  }

  private static validate(reqObj: Record<string, unknown>, errorId: string | null): JsonRpcResult<JsonRpcMessageBody> {
    if (reqObj['jsonrpc'] !== '2.0') {
      return jsonRpcFailure(
        errorId,
        invalidRequestError('unsupported_jsonrpc_version', `unsupported jsonrpc version: ${String(reqObj['jsonrpc'])}`)
      );
    }

    const method = reqObj['method'];
    if (typeof method !== 'string') {
      return jsonRpcFailure(
        errorId,
        invalidRequestError('invalid_method_type', `invalid method type: ${typeof method}`)
      );
    }

    const params = reqObj['params'];
    if (params !== undefined && (params === null || typeof params !== 'object' || Array.isArray(params))) {
      return jsonRpcFailure(
        errorId,
        invalidParamsError('invalid_params_type', `invalid params: ${JSON.stringify(params)}`)
      );
    }

    return { ok: true, value: { method, params: params as Record<string, unknown> | undefined } };
  }
}
